import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { supabase } from '@/lib/supabase';
import { useProfile } from '@/lib/providers';
import { useToast } from '@/components/ui/use-toast';
import type { Database } from '@/types/supabase';

type ChatSource = Database['public']['Enums']['chat_source'];

interface ReportMessageDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  messageId: string;
  reportedUserId: string;
  source: ChatSource;
}

export function ReportMessageDialog({
  open,
  onOpenChange,
  messageId,
  reportedUserId,
  source,
}: ReportMessageDialogProps) {
  const { profile } = useProfile();
  const { toast } = useToast();
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!profile?.id) return;
    setSubmitting(true);
    const { error } = await supabase.from('chat_reports').insert({
      reporter_id: profile.id,
      reported_user_id: reportedUserId,
      message_source: source,
      message_id: messageId,
      reason: reason.trim() || null,
    });
    setSubmitting(false);
    if (error) {
      console.error('Report failed:', error);
      toast({
        variant: 'destructive',
        title: 'Rapportage mislukt',
        description: 'Probeer het opnieuw.',
      });
      return;
    }
    toast({
      title: 'Bericht gerapporteerd',
      description: 'Onze moderators bekijken het zo snel mogelijk.',
    });
    setReason('');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !submitting && onOpenChange(v)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Bericht rapporteren</DialogTitle>
          <DialogDescription>
            Vertel ons kort waarom je dit bericht rapporteert. Onze moderators
            bekijken het en nemen zo nodig actie.
          </DialogDescription>
        </DialogHeader>
        <Textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Reden (optioneel)"
          rows={4}
          disabled={submitting}
        />
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            Annuleren
          </Button>
          <Button
            onClick={submit}
            disabled={submitting}
            className="bg-red-600 hover:bg-red-700"
          >
            {submitting ? 'Bezig...' : 'Rapporteer'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
